import React from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  ExternalLink,
  FileText,
  Languages,
  LockKeyhole,
  Megaphone,
  Sparkles,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  ADMIN_CAPABILITY_STATUS_META,
  ADMIN_CAPABILITY_VISIBILITY_EMPTY_TEST_ID,
  ADMIN_CAPABILITY_VISIBILITY_LOADING_TEST_ID,
  ADMIN_CAPABILITY_VISIBILITY_PANEL_TEST_ID,
  adminCapabilityCardTestId,
  buildAdminCapabilityCards,
} from '../../lib/adminCapabilityVisibility';

const ICONS = {
  activity: Activity,
  fileText: FileText,
  languages: Languages,
  lock: LockKeyhole,
  megaphone: Megaphone,
  sparkles: Sparkles,
};

const resolveText = (t, key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);

export default function AdminCapabilityVisibilityPanel({
  cards = buildAdminCapabilityCards(),
  t,
}) {
  if (cards === null) {
    return (
      <section
        className="mb-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
        data-testid={ADMIN_CAPABILITY_VISIBILITY_LOADING_TEST_ID}
      >
        <p className="text-sm text-slate-500">
          {resolveText(t, 'admin.capabilities.loading', 'Loading capability visibility...')}
        </p>
      </section>
    );
  }

  if (!cards.length) {
    return (
      <section
        className="mb-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
        data-testid={ADMIN_CAPABILITY_VISIBILITY_EMPTY_TEST_ID}
      >
        <p className="text-sm text-slate-500">
          {resolveText(t, 'admin.capabilities.empty', 'No capability visibility records are available.')}
        </p>
      </section>
    );
  }

  return (
    <section className="mb-8" data-testid={ADMIN_CAPABILITY_VISIBILITY_PANEL_TEST_ID}>
      <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold text-slate-800">
            {resolveText(t, 'admin.capabilities.heading', 'Capability visibility')}
          </h2>
          <p className="text-sm text-slate-600">
            {resolveText(
              t,
              'admin.capabilities.intro',
              'Read-only deployment posture for built or planned JCEOS/TLA capabilities.',
            )}
          </p>
        </div>
        <Badge className="w-fit border-slate-200 bg-slate-100 text-slate-700">
          {resolveText(t, 'admin.capabilities.read_only_badge', 'Read-only')}
        </Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => {
          const Icon = ICONS[card.icon] || Activity;
          const status = ADMIN_CAPABILITY_STATUS_META[card.status] || ADMIN_CAPABILITY_STATUS_META['deployed-hidden'];

          return (
            <article
              key={card.id}
              className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
              data-testid={adminCapabilityCardTestId(card.id)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="truncate font-semibold text-slate-800">
                      {resolveText(t, card.titleKey, card.title)}
                    </h3>
                    <p className="text-xs text-slate-500">
                      {resolveText(t, card.postureKey, card.posture)}
                    </p>
                  </div>
                </div>
                <Badge className={`shrink-0 ${status.className}`}>
                  {resolveText(t, status.labelKey, status.label)}
                </Badge>
              </div>

              <p className="mt-4 text-sm leading-6 text-slate-600">
                {resolveText(t, card.descriptionKey, card.description)}
              </p>
              <p className="mt-3 text-xs leading-5 text-slate-500">
                {resolveText(t, card.detailKey, card.detail)}
              </p>

              <div className="mt-4 flex min-h-8 items-center">
                {card.href ? (
                  <Button asChild variant="outline" size="sm">
                    <Link to={card.href} data-testid={`${adminCapabilityCardTestId(card.id)}-link`}>
                      {resolveText(t, card.actionLabelKey, card.actionLabel)}
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    </Link>
                  </Button>
                ) : (
                  <span className="text-xs font-medium text-slate-500">
                    {resolveText(t, 'admin.capabilities.no_safe_route', 'No safe route exposed')}
                  </span>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
